import React, { useState } from "react";
import {
  GuideCloseButton,
  titleStyle,
  introStyle,
  sectionStyle,
  h2Style,
  listStyle,
  PromptBlock,
} from "./dashboardSkillGuideCommon";

const EXISTING_REPO_SETUP_PROMPT = `Use the dashboard-skill to integrate the Munshot Dashboard SDK into an existing dashboard repository.

Before making any changes, read the dashboard skill from the \`dashboard-template\` repository and treat it as the single source of truth. The dashboard-template repository exists only as a read-only reference for the dashboard skill.

Target repository:
<replace with your existing dashboard repository name>

Important:

- Work ONLY in the target repository above.
- NEVER edit, commit to, rename, move, delete, or otherwise modify the \`dashboard-template\` repository.
- Treat everything in the \`dashboard-template\` repository (especially \`.claude/skills/\`) as read-only reference material.
- Read the skill from the dashboard-template repository, but implement all changes only in the target repository.

Your primary objective is to correctly integrate the Munshot Dashboard SDK into the existing dashboard.

Before writing any code, read and follow \`auth-standards.md\` exactly.

Also read \`embed_protection.md\` and add both embed-protection files to the target repository.

Also read \`chat-agent.md\` and add the "Ask AI" chat panel, following that file exactly.

In particular:

- Follow the host handshake exactly as documented.
- Use the \`useHostContext\` pattern exactly as documented.
- Follow the \`lib/sdk.ts\` implementation pattern.
- Correctly receive the session token and selected ticker from the host.
- Authenticate every API request with:
  Authorization: Bearer \${session.token}
- Implement the \`dashboard.capture.visual\` and \`dashboard.capture.snapshot\` handlers exactly as described.
- Obey every "DO NOT" rule in \`auth-standards.md\`.
- Never set \`autoReady: false\`.
- Never call \`sdk.ready()\` manually.
- Do not assume anything about the host or SDK that is not documented.
- Do not hallucinate missing behavior. If something is unclear, rely only on the documented implementation patterns from the skill.

The existing dashboard is already implemented. Your primary task is to integrate the Munshot Dashboard SDK correctly, not to rebuild the dashboard.

Requirements:

- Preserve the existing dashboard functionality unless required for the SDK integration.
- Do not make unrelated refactors.
- Do not replace existing business logic unless necessary for compatibility with the SDK.
- Ensure the project builds successfully with no type errors after the integration.
- Add the embed-protection files from \`embed_protection.md\`. If the repo already has a \`_headers\` or a root \`functions/_middleware.js\`, merge the new rules into it instead of replacing it. Place each file where it takes effect for this repo's deploy setup, and tell me which paths you used.
- Add the "Ask AI" chat panel from \`chat-agent.md\` once the SDK integration is in place (\`runAgent.ts\` imports \`sdk\` from \`lib/sdk.ts\`). Copy \`prompts.ts\`, \`runAgent.ts\` and \`AskAiPanel.tsx\` verbatim; write \`dashboardContext.ts\` and \`tools.ts\` against this dashboard's existing fetch functions and live filter state — do not add a second API client. Keep the tool catalog read-only. This panel is an intended addition; the "no unrelated refactors" rule still applies to everything else.
- The panel needs \`react-markdown\` and \`remark-gfm\` (never \`rehype-raw\`). Install them and confirm they work with this repo's React version before building.
- When finished, summarize the SDK/authentication changes you made and any manual follow-up steps, if required.`;

const formCardStyle = {
  backgroundColor: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  padding: "18px",
  marginBottom: "18px",
};

const labelStyle = {
  display: "block",
  color: "#1e293b",
  fontSize: "14px",
  fontWeight: "700",
  marginBottom: "6px",
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #cbd5e1",
  borderRadius: "6px",
  padding: "10px 12px",
  color: "#1e293b",
  fontSize: "14px",
  marginBottom: "12px",
};

const buttonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "40px",
  padding: "0 14px",
  backgroundColor: "#3730a3",
  color: "#ffffff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontWeight: "700",
  fontSize: "14px",
};

const errorStyle = {
  color: "#b91c1c",
  fontSize: "13px",
  fontWeight: "600",
  marginTop: "8px",
};

const generatedPromptNoteStyle = {
  backgroundColor: "#eef2ff",
  border: "1px solid #c7d2fe",
  borderRadius: "8px",
  padding: "12px 14px",
  color: "#312e81",
  fontSize: "13px",
  lineHeight: 1.55,
  marginBottom: "12px",
};

const buildExistingRepoPrompt = (repoName) =>
  EXISTING_REPO_SETUP_PROMPT.replace(
    "<replace with your existing dashboard repository name>",
    repoName.trim(),
  );

const DashboardSkillSetupExistingRepos = ({ onClose }) => {
  const [repoName, setRepoName] = useState("");
  const [generatedPrompt, setGeneratedPrompt] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleGeneratePrompt = () => {
    const trimmedRepoName = repoName.trim();

    if (!trimmedRepoName) {
      setErrorMessage("Enter the repository name exactly before generating the prompt.");
      setGeneratedPrompt("");
      return;
    }

    setErrorMessage("");
    setGeneratedPrompt(buildExistingRepoPrompt(trimmedRepoName));
  };

  return (
  <div>
    <GuideCloseButton onClose={onClose} />

    <h2 style={titleStyle}>Dashboard Skill Setup For Existing Repositories</h2>
    <p style={introStyle}>
      Use this guide if you already have a dashboard repository and want to use the{" "}
      <code>dashboard-skill</code> in it.
    </p>

    <section style={sectionStyle}>
      <h3 style={h2Style}>Step 1: Open Claude Code</h3>
      <ol style={listStyle}>
        <li>Create a new Claude Code session.</li>
        <li>
          Select:
          <ul style={{ ...listStyle, marginTop: "8px" }}>
            <li>Your existing dashboard repository</li>
            <li>
              The <code>dashboard-template</code> repository (Click on <code>+</code> icon above the prompt box
              and add it)
            </li>
          </ul>
        </li>
      </ol>
    </section>

    <section style={sectionStyle}>
      <h3 style={h2Style}>Step 2: Give Claude The Setup Prompt</h3>
      <div style={formCardStyle}>
        <label style={labelStyle} htmlFor="existing-repo-name">
          Existing dashboard repository name (required)
        </label>
        <input
          id="existing-repo-name"
          value={repoName}
          onChange={(event) => setRepoName(event.target.value)}
          style={inputStyle}
          placeholder="Enter the repository name exactly"
        />
        <button type="button" style={buttonStyle} onClick={handleGeneratePrompt}>
          Generate Prompt
        </button>
        {errorMessage ? <div style={errorStyle}>{errorMessage}</div> : null}
      </div>
      {generatedPrompt ? (
        <>
          <p style={generatedPromptNoteStyle}>
            <strong>Note:</strong> Use this generated prompt only once, at the
            start of the Claude Code session. For all later changes,
            improvements, or fixes, continue in your own natural language and
            build iteratively.
          </p>
          <PromptBlock give={generatedPrompt} />
        </>
      ) : null}
    </section>
  </div>
  );
};

export default DashboardSkillSetupExistingRepos;
